/**
 * `people:` / `p:` / `@` (PLAN §2.4, §3.6, S2). Name words in any order, the
 * e-mail and its local part. Enter opens the DM, or creates it. Local rows
 * come first, synchronously; the server's name search among related users
 * (§12.3.1) follows after 200 ms for ≥ 2 characters and is aborted by the
 * next keystroke. Offline (or an old server that answers `[]` for a name)
 * simply means no server rows.
 */
import { AtSign, MessageSquare, Mail } from 'lucide-react';
import { ORDER } from './order';
import { selectTop } from '../localSearch';
import { copyWithNotice } from '../actions';
import { abortError, isAbortError } from '../types';
import { isCompleteEmail } from '../../components/chatHelpers';
import i18n from '../../i18n';

export const SERVER_DEBOUNCE_MS = 200;
export const SERVER_MIN_CHARS = 2;

/** setTimeout that rejects with AbortError when the signal fires. */
export function abortableSleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const id = setTimeout(resolve, ms);
    signal?.addEventListener?.('abort', () => {
      clearTimeout(id);
      reject(abortError());
    }, { once: true });
  });
}

/**
 * @param {{ index: ReturnType<import('./peopleIndex').createPeopleIndex>,
 *           searchUsers?: (q: string, opts: {limit:number, signal:AbortSignal}) => Promise<object[]>,
 *           openChat: (target: {channelId?: any, userId?: any}) => Promise<any>,
 *           loadTeamRosters?: () => void, debounceMs?: number }} deps
 */
export default function create(deps) {
  const { index } = deps;
  const debounceMs = deps.debounceMs ?? SERVER_DEBOUNCE_MS;
  return {
    id: 'people',
    prefixes: ['people', 'p', 'person', 'user', 'member'],
    sigils: ['@'],
    labelKey: 'palette.scope.people',
    icon: AtSign,
    order: ORDER.people,
    page: 'chat.panel',
    kind: 'local-sync',
    available: (ctx) => !!ctx.currentUserId,
    async *search(req) {
      deps.loadTeamRosters?.();
      const all = index.all();
      const local = selectTop(all, req, { emptyOrder: (a, b) => (b.ts || 0) - (a.ts || 0) || a.title.localeCompare(b.title) });
      yield local;
      const text = req.text;
      if (!deps.searchUsers || !req.ctx.online || text.length < SERVER_MIN_CHARS) return;
      try {
        await abortableSleep(debounceMs, req.signal);
        const users = await deps.searchUsers(text, { limit: Math.min(25, req.limit), signal: req.signal });
        if (req.signal.aborted) return;
        const fresh = index.addPeople(users || [], 'server');
        if (!fresh.length) return;
        // Server rows are related users by contract; the e-mail path is an exact address.
        const rows = fresh.map((id) => index.get(id)).filter(Boolean);
        yield isCompleteEmail(text) ? rows : selectTop(rows, req);
      } catch (err) {
        if (isAbortError(err) || req.signal.aborted) return;
        // Offline / server error: the local rows stand on their own (§2.6).
      }
    },
    async recent(ids) {
      return ids.map((id) => index.get(id)).filter(Boolean);
    },
    actions(result) {
      const out = [
        {
          id: 'message',
          titleKey: 'palette.action.message',
          icon: MessageSquare,
          run: (r) =>
            deps.openChat(r.payload.channelId ? { channelId: r.payload.channelId } : { userId: r.payload.userId }).catch((e) => {
              // The palette has closed: no inline surface, so a diagnosable log line (never the query).
              // eslint-disable-next-line no-console
              console.error('palette: could not open the conversation', e && e.name);
            }),
        },
      ];
      if (result.payload?.email) {
        out.push({ id: 'copyEmail', titleKey: 'palette.action.copyEmail', icon: Mail, run: (r) => copyWithNotice(r.payload.email, i18n.t('palette.notice.email')) });
      }
      return out;
    },
  };
}
